import { createContext, useContext, useEffect, useState } from "react";

import { Configuration } from "../utils/configuration";

export type Idp = "okta" | "entra";

type DiscoveryStatus = "loading" | "ready" | "error";

interface IdpContextValue {
  idp: Idp;
}

const IdpContext = createContext<IdpContextValue | undefined>(undefined);

interface IdpDiscoveryProviderProps {
  children: React.ReactNode;
}

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  gap: "1rem",
  fontFamily: "sans-serif",
};

/**
 * Resolves the active identity provider from the gateway discovery endpoint
 * before any IdP-specific provider is mounted. The call runs once on load;
 * a hard page reload re-runs it. Renders a loading state while resolving and
 * an error state if the endpoint is unreachable — it never falls back to a
 * default IdP.
 */
export const IdpDiscoveryProvider = ({ children }: IdpDiscoveryProviderProps) => {
  const [status, setStatus] = useState<DiscoveryStatus>("loading");
  const [idp, setIdp] = useState<Idp>();

  useEffect(() => {
    let cancelled = false;

    const resolveIdp = async () => {
      if (!Configuration.gatewayUrl || !Configuration.gatewayBasicAuthToken) {
        if (!cancelled) setStatus("error");
        return;
      }

      try {
        const url = `${Configuration.gatewayUrl}/auth/idp?app=${encodeURIComponent(
          Configuration.appName
        )}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Basic ${Configuration.gatewayBasicAuthToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`IdP discovery failed with status ${response.status}`);
        }

        const data = (await response.json()) as { idp?: string };
        if (data.idp !== "okta" && data.idp !== "entra") {
          throw new Error(`Unexpected IdP value: ${String(data.idp)}`);
        }

        if (!cancelled) {
          setIdp(data.idp);
          setStatus("ready");
        }
      } catch (error) {
        console.error("[IdpDiscoveryProvider] Failed to resolve IdP:", error);
        if (!cancelled) setStatus("error");
      }
    };

    resolveIdp();

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") {
    return (
      <div style={containerStyle}>
        <p>Loading…</p>
      </div>
    );
  }

  if (status === "error" || !idp) {
    return (
      <div style={containerStyle}>
        <h2>Unable to start</h2>
        <p>
          Could not determine the authentication provider. Please try again
          later or contact support.
        </p>
      </div>
    );
  }

  return <IdpContext.Provider value={{ idp }}>{children}</IdpContext.Provider>;
};

/**
 * Returns the resolved identity provider. Only valid inside a subtree where
 * IdpDiscoveryProvider has resolved (i.e. children of a ready provider).
 */
export const useIdp = (): Idp => {
  const context = useContext(IdpContext);
  if (!context) {
    throw new Error("useIdp must be used within a resolved IdpDiscoveryProvider");
  }
  return context.idp;
};
